export const IMAGE_ASPECT_RATIOS = Object.freeze({
  "1:1": 1,
  "4:1": 4,
  "3:4": 3 / 4,
  "16:9": 16 / 9,
});

function normalizeCropAnchor(value) {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0.5;
}

export function calculateImageCropArea(
  sourceWidth,
  sourceHeight,
  targetRatio,
  anchorX = 0.5,
  anchorY = 0.5
) {
  if (
    !Number.isFinite(sourceWidth) ||
    !Number.isFinite(sourceHeight) ||
    !Number.isFinite(targetRatio) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0 ||
    targetRatio <= 0
  ) {
    throw new Error("invalid_crop_dimensions");
  }

  const sourceRatio = sourceWidth / sourceHeight;
  const width = Math.max(
    1,
    sourceRatio > targetRatio
      ? Math.round(sourceHeight * targetRatio)
      : sourceWidth
  );
  const height = Math.max(
    1,
    sourceRatio > targetRatio
      ? sourceHeight
      : Math.round(sourceWidth / targetRatio)
  );

  return {
    x: Math.round((sourceWidth - width) * normalizeCropAnchor(anchorX)),
    y: Math.round((sourceHeight - height) * normalizeCropAnchor(anchorY)),
    width,
    height,
  };
}

export function cropImageByAspect(
  file,
  targetRatio,
  anchorX = 0.5,
  anchorY = 0.5
) {
  return new Promise((resolve, reject) => {
    const browserWindow = typeof window !== "undefined" ? window : null;
    const urlApi = browserWindow?.URL;
    const ImageConstructor = browserWindow?.Image;
    const FileConstructor = browserWindow?.File;
    const documentApi = browserWindow?.document;

    if (
      !file ||
      !urlApi?.createObjectURL ||
      !ImageConstructor ||
      !FileConstructor ||
      !documentApi
    ) {
      reject(new Error("image_editor_unavailable"));
      return;
    }

    const sourceUrl = urlApi.createObjectURL(file);
    let sourceUrlReleased = false;
    const releaseSourceUrl = () => {
      if (sourceUrlReleased) return;
      sourceUrlReleased = true;
      urlApi.revokeObjectURL(sourceUrl);
    };
    const image = new ImageConstructor();

    image.onload = () => {
      try {
        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;
        const cropArea = calculateImageCropArea(
          sourceWidth,
          sourceHeight,
          targetRatio,
          anchorX,
          anchorY
        );
        const canvas = documentApi.createElement("canvas");
        canvas.width = cropArea.width;
        canvas.height = cropArea.height;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("canvas_context_unavailable");

        context.drawImage(
          image,
          cropArea.x,
          cropArea.y,
          cropArea.width,
          cropArea.height,
          0,
          0,
          cropArea.width,
          cropArea.height
        );

        canvas.toBlob(
          (blob) => {
            releaseSourceUrl();
            if (!blob) {
              reject(new Error("image_blob_failed"));
              return;
            }

            try {
              const croppedFile = new FileConstructor([blob], file.name, {
                type: blob.type || file.type || "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(croppedFile);
            } catch (error) {
              reject(error);
            }
          },
          file.type || "image/jpeg",
          0.95
        );
      } catch (error) {
        releaseSourceUrl();
        reject(error);
      }
    };

    image.onerror = () => {
      releaseSourceUrl();
      reject(new Error("image_load_failed"));
    };
    image.src = sourceUrl;
  });
}
